import { Box, Divider, TextField } from "@mui/material";
import { SectionHeader } from "apps/web/src/componentLibrary/SectionHeader";
import LabeledSelect from "apps/web/src/components/universal/LabeledSelect";
import {
  GoalConfigurationTemplateTableItemFragment,
  useGoalConfigurationTemplateOffsetConnectionQuery,
} from "apps/web/src/types";
import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { GoalConfigurationFormType } from "../GoalConfigurationSchema";

const TEMPLATE_PAGE_SIZE = 100;

const SET_VALUE_OPTIONS = { shouldDirty: true, shouldValidate: true } as const;

export const applyTemplateToGoalForm = (
  form: UseFormReturn<GoalConfigurationFormType>,
  template: GoalConfigurationTemplateTableItemFragment
): void => {
  const { setValue } = form;
  setValue("task", template.name, SET_VALUE_OPTIONS);
  setValue(
    "type",
    {
      id: template.category.id,
      name: template.category.name,
      defaultEmoji: template.category.defaultEmoji,
    },
    SET_VALUE_OPTIONS
  );
  if (template.target) {
    setValue("completionCriteria.markAsComplete", false, SET_VALUE_OPTIONS);
    setValue(
      "completionCriteria.target",
      { value: template.target.value, unit: template.target.unit },
      SET_VALUE_OPTIONS
    );
  } else {
    setValue("completionCriteria.markAsComplete", true, SET_VALUE_OPTIONS);
    setValue("completionCriteria.target", undefined, SET_VALUE_OPTIONS);
  }
};

interface GoalConfigurationTemplateSelectorProps {
  form: UseFormReturn<GoalConfigurationFormType>;
}

export const GoalConfigurationTemplateSelector = ({ form }: GoalConfigurationTemplateSelectorProps) => {
  const { data, loading } = useGoalConfigurationTemplateOffsetConnectionQuery({
    variables: {
      pagination: { limit: TEMPLATE_PAGE_SIZE, offset: 0 },
      filterOptions: { searchText: null, target: null, uploadedByMe: null, categoryId: null },
    },
  });
  const templates = data?.goalConfigurationTemplatesOffsetConnection.edges ?? [];
  const [selectedTemplate, setSelectedTemplate] = useState<GoalConfigurationTemplateTableItemFragment | null>(null);

  const handleSelect = (template: GoalConfigurationTemplateTableItemFragment) => {
    setSelectedTemplate(template);
    applyTemplateToGoalForm(form, template);
  };

  const renderField = () => {
    if (loading) {
      return <TextField label="Template" value="Loading..." disabled fullWidth />;
    }
    if (templates.length === 0) {
      return <TextField label="Template" value="No Template" disabled fullWidth />;
    }
    return (
      <LabeledSelect
        placeholder="Template"
        optionToName={t => `${t.category.defaultEmoji} ${t.name}`}
        selectedOption={selectedTemplate}
        options={[...templates]}
        onChange={handleSelect}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <SectionHeader
        header="Template (optional)"
        subcopy="Use a template to pre-populate the details below and make changes as needed"
      />
      <Box sx={{ maxWidth: 400 }}>{renderField()}</Box>
      <Divider />
    </Box>
  );
};
